import { ClockIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import { ExpandableJsonPanel } from '#components/ExpandableJsonPanel'
import { formatDateTime, formatDuration, getDurationColor } from '#utils/format'

import type { TransactionWithCategory } from '../types'

export const TraceRow = ({
  transaction,
}: {
  transaction: TransactionWithCategory
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasError = !!transaction.error
  const tagColor = hasError
    ? 'bg-error/20 text-error'
    : 'bg-success/20 text-success'

  return (
    <div className="rounded-box bg-neutral border border-base-300">
      <div
        className="p-4 cursor-pointer hover:bg-base-300/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div
              className={`px-2 py-1 rounded text-xs font-semibold ${tagColor} flex items-center gap-1.5`}
            >
              <ClockIcon className="w-3 h-3" />
              {hasError ? 'ERROR' : 'SUCCESS'}
            </div>
            <span className="font-semibold">
              {transaction.category}{' '}
              {transaction.layer === 'carrier' && (
                <span className="badge badge-info badge-outline text-sm uppercase ml-4">
                  {transaction.id}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-base-content/70">
              <span className="font-medium">Time:</span>{' '}
              <span className="font-mono text-xs">
                {formatDateTime(transaction.timeStamp)}
              </span>
            </div>
            <div
              className={`font-mono font-bold ${getDurationColor(transaction.duration)}`}
            >
              {formatDuration(transaction.duration)}
            </div>
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {transaction.request && (
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <h5 className="font-semibold text-blue-300 text-sm">Request</h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={transaction.request}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-blue-500/20 font-mono text-blue-100"
                defaultExpanded={false}
              />
            </div>
          )}

          {transaction.response && (
            <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <h5 className="font-semibold text-green-300 text-sm">
                  Response
                </h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={transaction.response}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-green-500/20 font-mono text-green-100"
                defaultExpanded={false}
              />
            </div>
          )}

          {transaction.error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <h5 className="font-semibold text-red-300 text-sm">Error</h5>
              </div>
              <ExpandableJsonPanel
                title=""
                data={transaction.error}
                maxHeight="max-h-64"
                preClassName="bg-slate-950/80 border border-red-500/20 font-mono text-red-100"
                defaultExpanded={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
